import React from "react";
import { Box } from "@mui/material";
import { statusOfServices } from "constants/serviceDetails";
import { getServiceTimeSegments } from "../utils/serviceTime";
import { minutesToHours } from "../utils/formatUtils";

const barLabelStyles = {
	display: "flex",
	alignItems: "center",
	height: "100%",
	padding: "0 8px",
	color: "#fff",
	fontSize: "12px",
	fontWeight: 500,
	cursor: "pointer",
	width: "100%",
};

const labelTextStyles = {
	overflow: "hidden",
	textOverflow: "ellipsis",
	whiteSpace: "nowrap",
};

// The label sits over green, grey and red in turn, so it needs to stay
// readable against all three
const serviceLabelStyles = {
	...barLabelStyles,
	position: "relative",
	textShadow: "0 1px 2px rgba(0, 0, 0, 0.6)",
};

const greyBackedLabelStyles = {
	...barLabelStyles,
	position: "relative",
	color: "#333",
};

// The label starts at the left edge, so it sits on grey unless the green
// segment reaches past it
const MIN_USED_PERCENT_UNDER_LABEL = 30;

export const getServiceLabelStyles = (segments) =>
	(segments?.used || 0) >= MIN_USED_PERCENT_UNDER_LABEL
		? serviceLabelStyles
		: greyBackedLabelStyles;

const segmentTrackStyles = {
	position: "absolute",
	inset: 0,
	display: "flex",
	borderRadius: "inherit",
	overflow: "hidden",
};

const SEGMENT_COLOURS = {
	used: "#23BB79",
	unused: "#D5D7DB",
	overrun: "#E31212",
};

const formatHours = (minutes) => `${minutesToHours(minutes)} hrs`;

const buildServiceTitle = (data, breakdown) => {
	const status = data.status || "S";
	const statusName = statusOfServices(status, data.serviceData?.tasksSkipped || false);
	const lines = [
		`Status: ${statusName}`,
		`Role: ${data.serviceData?.role || "Role"}`,
	];

	if (data.interval) lines.push(`Interval: ${data.interval}`);
	if (breakdown.estimated) lines.push(`Estimated: ${formatHours(breakdown.estimated)}`);
	if (breakdown.elapsed) lines.push(`Actual: ${formatHours(breakdown.elapsed)}`);
	if (breakdown.unused) lines.push(`Unused: ${formatHours(breakdown.unused)}`);
	if (breakdown.overrun) lines.push(`Over by: ${formatHours(breakdown.overrun)}`);
	if (data.progress !== undefined) lines.push(`Progress: ${data.progress}%`);

	return lines.join("\n");
};

const GanttTaskContent = ({ data }) => {
	const isWorkOrder = !data.parent;
	const isService = data.parent && data.serviceData;

	if (isWorkOrder) {
		const workOrderText = data.workOrder || data.text || "";
		let titleText = `Work Order: ${workOrderText}`;
		if (data.interval) titleText += `\nInterval: ${data.interval}`;
		if (data.estimatedMinutes) titleText += `\nEst. Time: ${data.estimatedMinutes} hrs`;

		return (
			<Box
				sx={barLabelStyles}
				title={titleText}
				data-wo-status={data.statusColor || "ontrack"}
			>
				<span style={labelTextStyles}>{workOrderText}</span>
			</Box>
		);
	}

	if (isService) {
		const breakdown = data.timeBreakdown;
		const segments = breakdown ? getServiceTimeSegments(breakdown) : null;
		const role = data.serviceData?.role || "Role";

		return (
			<Box
				sx={{
					position: "relative",
					height: "100%",
					width: "100%",
					// Covers the bar, so this wins over the col-resize cursor the
					// library sets near the edges. These bars move, never resize.
					cursor: data.isDraggable ? "grab" : "pointer",
				}}
			>
				{segments && (
					<Box sx={segmentTrackStyles} aria-hidden>
						<Box sx={{ width: `${segments.used}%`, backgroundColor: SEGMENT_COLOURS.used }} />
						<Box sx={{ width: `${segments.unused}%`, backgroundColor: SEGMENT_COLOURS.unused }} />
						<Box sx={{ width: `${segments.overrun}%`, backgroundColor: SEGMENT_COLOURS.overrun }} />
					</Box>
				)}

				<Box
					sx={getServiceLabelStyles(segments)}
					title={buildServiceTitle(data, breakdown || {})}
				>
					<span style={labelTextStyles}>{role}</span>
				</Box>
			</Box>
		);
	}

	return <span>{data.text || ""}</span>;
};

export default GanttTaskContent;
